import { StoreOutlined } from "@mui/icons-material";
import { get, isNil } from "lodash";
import React, { useMemo } from "react";
import { useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import RffAddressEditingField from "../../components/finalForm/inputs/RffAddressEditingField";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffSelectRadioField from "../../components/finalForm/inputs/RffSelectRadioField";
import RffSelectToggleField from "../../components/finalForm/inputs/RffSelectToggleField";
import RffConditional from "../../components/finalForm/shared/RffConditional";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import TitledLoanSection from "./TitledLoanSection";
import { selectOptionsPropertyType } from "../../constants/selectOptions/selectOptionsPropertyType";

const PropertiesSection = ({ returnChildrenOnly }) => {
  const [expanded, setExpanded] = React.useState(0);
  const { values } = useFormState();
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { change } = useForm();

  const onAddressChange = (address) => {
    const propertyName = address?.fullAddress;
    if (isNil(values?.loanName) && propertyName) {
      change(`loanName`, propertyName);
    }
  };
  return (
    <TitledLoanSection
      // suppressCard
      icon={<StoreOutlined className="thin" />}
      variant="h2"
      collapsable
      returnChildrenOnly={returnChildrenOnly}
      title="Property Details"
    >
      <FieldArray name={"subjectProperties"}>
        {({ fields: arrayFields, meta }) => {
          return (
            <React.Fragment>
              {arrayFields.map((fieldName, index) => (
                <React.Fragment key={fieldName}>
                  <>
                    <RffGroup>
                      <RffAddressEditingField
                        name={`${fieldName}.address`}
                        label="Enter the subject property address"
                        onChange={onAddressChange}
                        required
                      />
                      {/* <RffAddressInputField
                        name={`${fieldName}.address`}
                        label="Enter the subject property address"
                        onChange={onAddressChange}
                        required
                      /> */}
                    </RffGroup>
                    <PropertyPurchase fieldName={fieldName} />
                    <PropertyConditionSection fieldName={fieldName} />
                  </>
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        }}
      </FieldArray>
    </TitledLoanSection>
  );
};

const PropertyPurchase = ({ fieldName }) => {
  const { values } = useFormState();

  if (values?.loanPurpose !== "Purchase") {
    return null;
  }

  return (
    <RffGroup title="Property Purchase Details" suppressGrid>
      <RffConditional field="loanPurpose" value={"Purchase"}>
        <RffDollarField
          name={`${fieldName}.purchasePrice`}
          label="What is the property purchase price?"
          // size={6}
        />
        <RffDateField
          // size={6}
          name={`${fieldName}.purchaseDate`}
          label="What is the expected purchase date for the property?"
        />
        <RffDollarField
          // size={6}
          name={`${fieldName}.constructionBudget`}
          label="Total construction budget for the project"
        />
        <RffDollarField
          name={`${fieldName}.arv`}
          // size={6}
          label="After Renovation Value"
        />
      </RffConditional>

      <RffConditional
        field="loanPurpose"
        operator={"includes"}
        value={[
          "Refinance - No Cash Out",
          "Refinance - Cash Out",
          "Delayed Purchase",
        ]}
      >
        <RffDateField
          name={`${fieldName}.originalPurchasePrice`}
          label="What was original purchase price for the property?"
        />
        <RffDateField
          name={`${fieldName}.originalPurchaseDate`}
          label="What was original purchase date of the property?"
        />
      </RffConditional>
    </RffGroup>
  );
};

const PropertyConditionSection = ({ fieldName }) => {
  return (
    <RffGroup
      title="Property Condition & Details"
      suppressGrid
      suppressBottomMargin
    >
      <RffSelectToggleField
        name={`${fieldName}.propertyType`}
        label="Property Type"
        options={selectOptionsPropertyType}
        // options={[
        //   "SFR",
        //   "2-unit",
        //   "3-unit",
        //   "4-unit",
        //   // "5-unit",
        //   // "6-unit",
        //   // "7-unit",
        //   // "8-unit",
        //   // "Condo",
        //   // "Townhome",
        //   // "Planned Unit Development (PUD)",
        //   // "Multifamily",
        //   // "Mixed-Use",
        //   // "Commercial",
        //   // "Undeveloped Land",
        //   // "Other",
        // ]}
      />

      <RffConditional
        field="loanProductType"
        operator="includes"
        value={["Ground Up Construction", "Vacant Land"]}
      >
        <RffSelectToggleField
          name={`${fieldName}.proposedPropertyType`}
          label="Proposed Property Type"
          options={[
            "SFR",
            "2-unit",
            "3-unit",
            "4-unit",
            "Condo",
            "Townhome",
            "Planned Unit Development (PUD)",
            "Multifamily (5+ Units)",
            "Mixed-Use",
            "Commercial",
            "Other",
          ]}
        />
      </RffConditional>
      {/* <RffBooleanToggleField
        size={4}
        name={`${fieldName}.propertyRuralIndicator`}
        label="Is the property in a rural location?"
      /> */}
      <RffBooleanToggleField
        size={6}
        name={`${fieldName}.floodZone`}
        label="Is the property in a flood zone?"
      />

      <PropertyListedForSaleRow fieldName={fieldName} />
      {/* <PropertSquareFootageRow fieldName={fieldName} /> */}
      {/* <RffSelectToggleField
        name={`${fieldName}.propertyConditionAtOrigination`}
        label="Property Condition At Origination"
        options={selectOptionsPropertyConditionAtOrigination}
      /> */}
      <RffConditional field="loanProductType" value={"Fix and Flip"}>
        <RffSelectRadioField
          name={`${fieldName}.ffPropertyStatus`}
          label="How would you describe the current property status for this fix and flip project?"
          options={[
            "Requires significant renovation",
            "Average condition - requires moderate renovation",
            "Above Average condition - requires limited renovation",
          ]}
        />
      </RffConditional>

      <RffConditional field="loanProductType" value={"Ground Up Construction"}>
        <RffSelectRadioField
          name={`${fieldName}.gucPropertyStatus`}
          label="How would you describe the current project status for this ground up project?"
          options={[
            "Raw land without approved plans and/or utilities",
            "Raw land with approved plans and utilities in place",
            "Foundation in place",
            "Significant Progress to Exterior, limited or no exterior work complete",
            "Finishing Interior",
          ]}
        />
      </RffConditional>
      {/* <PropertSquareFootageRow fieldName={fieldName} /> */}
    </RffGroup>
  );
};

const PropertyListedForSaleRow = ({ fieldName }) => {
  const { values } = useFormState();

  const residentialSqFootageSize = useMemo(() => {
    const lpt = values?.loanProductType;
    if (
      lpt === "Fix and Flip" ||
      lpt === "Ground Up Construction" ||
      lpt === "Vacant Land"
    ) {
      return 6;
    }

    return 12;
  }, [values?.loanProductType]);

  const isListed = useMemo(() => {
    const name = `${fieldName}.propertyListedForSale`;
    console.log("Name: ", name);
    const val = get(values, name);
    console.log("val: ", val);

    if (val === true || val === "true") {
      return true;
    }
    return false;
  }, [values?.subjectProperties, fieldName]);
  return (
    <>
      <RffConditional
        field="loanPurpose"
        operator={"includes"}
        value={[
          "Refinance - No Cash Out",
          "Refinance - Cash Out",
          "Delayed Purchase",
        ]}
      >
        <RffBooleanToggleField
          name={`${fieldName}.propertyListedForSale`}
          label="Property Listed For Sale"
          size={4}
        />
        {isListed ? (
          <>
            <RffDateField
              name={`${fieldName}.lastListedForSaleDate`}
              label="Last Listed For Sale Date"
              size={6}
            />
            <RffDollarField
              name={`${fieldName}.lastListedForSalePrice`}
              label="Last Listed For Sale Price"
              size={6}
            />
          </>
        ) : null}
      </RffConditional>
    </>
  );
};

export default PropertiesSection;
